/*
 * Naturlabs Rituals page (sections/nl-rit-*.liquid, SPEC 5.6). Vanilla, no dependencies; loaded with defer after
 * nl-app.js (NL.cart, NL.formatMoney, NL.toast).
 *
 * Hero markers   [data-rit-marker]: bubble opens on hover (fine pointers), tap or keyboard focus; Esc and a click
 *                outside close it. [data-rit-pick="<ritual>"] sets the builder filter and scrolls to the builder
 *                (without a builder on the page it is a normal link to the Rituals page with ?r=<ritual>).
 * Builder        [data-rit-builder]: selection = one pack per product line (line_key), kept in localStorage
 *                'nl-ritual-v1' (every access in try/catch, the page works without storage). URL ?add=<handle>
 *                (comma list) preselects, ?r=<ritual> sets the filter.
 * Tiers          data-rit-tiers "2:5,3:10,4:15": number of different lines : %. Amounts in halers:
 *                subtotal = sum of prices, discount = sum of round(price * pct / 100) per line,
 *                payable = subtotal - discount in LIVE, subtotal in PREVIEW and OFF (nothing with a discount is
 *                promised or sent to the cart unless the discount is live).
 * Cart           NL.cart.add(items, { source: 'rituals' }) with hidden properties _nl_ritual and _nl_ritual_size,
 *                then the theme opens the cart drawer. LIVE check: when the cart shows no discount although one
 *                was expected, the panel says so and console.warn logs it.
 * Event          document 'nl:ritual:add' { lines, tier } for measurement.
 * Mobile         under 1024 px the panel is a bottom sheet opened from the bottom bar: backdrop, Esc, tap on the
 *                backdrop or drag down closes it, focus is trapped inside and the page does not scroll behind it.
 */
(() => {
  'use strict';

  const NL = (window.NL = window.NL || {});
  /* two sections include this file; run once */
  if (NL.ritualsBooted) return;
  NL.ritualsBooted = true;
  const STORE_KEY = 'nl-ritual-v1';
  const keyOf = (value) => typeof NL.productKey === 'function' ? NL.productKey(value) : null;
  const NBSP = ' ';
  const S = NL.ritualStrings || {};
  const locale = (NL.settings && NL.settings.locale) || document.documentElement.lang || 'cs';
  const tr = (key, values = {}) => String(S[key] || '').replace(/%(\w+)%/g, (all, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : all);
  const RITUALS = {
    'denni-zaklad': S.daily,
    'aktivni-den': S.active,
    'vecerni-rutina': S.evening,
  };
  const nutrientName = (key) => S[`nutrients.${key}`] || key;

  const $ = (selector, ctx = document) => ctx.querySelector(selector);
  const $$ = (selector, ctx = document) => Array.from(ctx.querySelectorAll(selector));
  const reduced = () =>
    NL.motion ? NL.motion.reduced : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mqDesktop = window.matchMedia('(min-width: 1024px)');
  const mqFine = window.matchMedia('(hover: hover) and (pointer: fine)');

  const decode = (value) => {
    const area = document.createElement('textarea');
    area.innerHTML = String(value || '');
    return area.value;
  };

  const money = (cents) => {
    const raw = typeof NL.formatMoney === 'function'
      ? NL.formatMoney(cents, { trimZeros: true }) : String(Number(cents || 0) / 100);
    return String(raw).replace(/ /g, NBSP);
  };

  const pluralRules = new Intl.PluralRules(locale);
  const countLabel = (n) => tr(`count.${pluralRules.select(n)}`, { count: n });
  const joinNutrients = (list) => new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(list);

  const storage = {
    read() {
      try {
        const value = JSON.parse(window.localStorage.getItem(STORE_KEY) || 'null');
        return value && typeof value === 'object' ? value : null;
      } catch (e) {
        return null;
      }
    },
    write(value) {
      try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(value));
      } catch (e) {
        /* storage blocked (private window, previews): keep the selection in memory */
      }
    },
  };

  const parseTiers = (raw) =>
    String(raw || '')
      .split(',')
      .map((pair) => pair.split(':').map((x) => Number(String(x).trim())))
      .filter(([n, p]) => n > 0 && p > 0)
      .map(([n, p]) => ({ n, p }))
      .sort((a, b) => a.n - b.n);

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };

  /* Name by the owner rule, one line: "Elements. Imunita, pokožka a vlasy" / "Vitamin C | Imunita a energie" */
  const inlineName = (variant) => {
    const wrap = el('span', 'nl-rit-name nl-rit-name--inline');
    const line = el('span', 'nl-rit-name__line');
    const raw = variant.line || variant.plain || '';
    const dot = raw.endsWith('.');
    line.textContent = dot ? raw.slice(0, -1) : raw;
    if (dot) line.append(el('span', 'nl-rit-name__dot', '.'));
    wrap.append(line);
    if (variant.benefit) {
      if (dot) {
        wrap.append(document.createTextNode(' '));
      } else {
        const bar = el('span', 'nl-rit-name__bar');
        bar.setAttribute('aria-hidden', 'true');
        wrap.append(bar, el('span', 'visually-hidden', ': '));
      }
      wrap.append(el('span', 'nl-rit-name__benefit', variant.benefit));
    }
    return wrap;
  };

  /* ==========================================================================
     Hero markers
     ========================================================================== */

  let leaveTimer = 0;
  const setOpen = (marker, open) => {
    marker.classList.toggle('is-open', open);
    const toggle = $('[data-rit-marker-toggle]', marker);
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
  };
  const closeAllMarkers = (except) =>
    $$('[data-rit-marker].is-open').forEach((m) => m !== except && setOpen(m, false));
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-rit-marker]')) closeAllMarkers(null);
  });
  document.addEventListener('nl:ritual:pick', () => closeAllMarkers(null));

  const initMarkers = () => {
    const markers = $$('[data-rit-marker]');
    if (!markers.length) return;
    const open = (marker) => {
      closeAllMarkers(marker);
      setOpen(marker, true);
    };

    markers.forEach((marker) => {
      const toggle = $('[data-rit-marker-toggle]', marker);
      if (!toggle || marker.dataset.ritBound) return;
      marker.dataset.ritBound = '1';
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        if (mqFine.matches) open(marker);
        else if (marker.classList.contains('is-open')) setOpen(marker, false);
        else open(marker);
      });
      marker.addEventListener('mouseenter', () => {
        if (!mqFine.matches) return;
        window.clearTimeout(leaveTimer);
        open(marker);
      });
      marker.addEventListener('mouseleave', () => {
        if (!mqFine.matches) return;
        window.clearTimeout(leaveTimer);
        leaveTimer = window.setTimeout(() => setOpen(marker, false), 240);
      });
      marker.addEventListener('focusin', (event) => {
        if (event.target === toggle && !toggle.matches(':focus-visible')) return;
        open(marker);
      });
      marker.addEventListener('focusout', (event) => {
        if (!marker.contains(event.relatedTarget)) setOpen(marker, false);
      });
      marker.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && marker.classList.contains('is-open')) {
          setOpen(marker, false);
          toggle.focus();
        }
      });
    });
  };

  const initVideo = () => {
    const video = $('[data-rit-video]');
    if (!video || video.dataset.ritBound || !('IntersectionObserver' in window)) return;
    video.dataset.ritBound = '1';
    const saveData = navigator.connection && navigator.connection.saveData;
    if (reduced() || saveData) return;
    // The photo under the video is the LCP: the video starts only after the page has loaded and the main thread is
    // idle, so its megabytes never compete with the first paint (perf lane 16. 10. 2026)
    const whenIdle = (fn) => {
      const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 2500 }) : window.setTimeout(fn, 600));
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    };
    whenIdle(() => observeVideo(video));
  };

  const observeVideo = (video) => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
            const playing = video.play();
            if (playing && typeof playing.then === 'function') {
              playing.then(() => video.classList.add('is-playing')).catch(() => {});
            }
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.4] }
    );
    observer.observe(video.closest('.nl-rit-stage') || video);
  };

  /* "Vybrat" in a bubble or a node card: filter + scroll when the builder is on this page, else follow the link */
  document.addEventListener('click', (event) => {
    const pick = event.target.closest('[data-rit-pick]');
    if (!pick || !$('[data-rit-builder]')) return;
    event.preventDefault();
    document.dispatchEvent(new CustomEvent('nl:ritual:pick', { detail: { key: pick.dataset.ritPick } }));
  });

  /* ==========================================================================
     Builder
     ========================================================================== */

  class RitualBuilder {
    constructor(root) {
      this.root = root;
      this.state = root.dataset.ritState || 'off';
      this.tiers = parseTiers(root.dataset.ritTiers);
      this.threshold = Number(root.dataset.ritThreshold) || 149900;
      this.ignore = new Set(
        String(root.dataset.ritIgnore || '')
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean)
      );
      this.lines = new Map();
      this.selected = new Map();
      this.filter = 'vse';
      this.busy = false;
      this.sheetOpen = false;
      this.lastTotals = '';

      this.cards = $$('[data-rit-card]', root);
      this.cards.forEach((card) => this.readCard(card));

      this.grid = $('[data-rit-grid]', root);
      this.summary = $('[data-rit-summary]', root);
      this.filterButtons = $$('[data-rit-filter]', root);
      this.filterDesc = $('[data-rit-filter-desc]', root);
      this.filterTexts = {};
      const tpl = $('template[data-rit-filter-texts]', root);
      if (tpl) {
        $$('[data-key]', tpl.content).forEach((span) => {
          this.filterTexts[span.dataset.key] = span.innerHTML;
        });
      }
      this.presets = $$('[data-rit-preset]', root);

      // JM-06: the filter row fades at the edge only while another option follows (phones)
      const seg = $('.nl-rit-filter__seg', root);
      if (seg) {
        const edge = () => seg.classList.toggle('is-more', seg.scrollWidth - seg.clientWidth - seg.scrollLeft > 4);
        seg.addEventListener('scroll', edge, { passive: true });
        window.addEventListener('resize', edge, { passive: true });
        edge();
      }

      this.panel = $('[data-rit-panel]', root);
      this.count = $('[data-rit-count]', root);
      this.empty = $('[data-rit-empty]', root);
      this.list = $('[data-rit-items]', root);
      this.slot = $('[data-rit-slot]', root);
      this.slotExtra = $('[data-rit-slot-extra]', root);
      this.ladder = $('[data-rit-ladder]', root);
      this.tierStatus = $('[data-rit-tier-status]', root);
      this.overlap = $('[data-rit-overlap]', root);
      this.overlapText = $('[data-rit-overlap-text]', root);
      this.totalsBox = $('[data-rit-totals]', root);
      this.ship = $('[data-rit-ship]', root);
      this.shipText = $('[data-rit-ship-text]', root);
      this.addButtons = $$('[data-rit-add]', root);
      this.addLabel = $('[data-rit-add-label]', root);
      this.note = $('[data-rit-note]', root);
      this.msgBox = $('[data-rit-msg]', root);
      this.bar = $('[data-rit-bar]', root);
      this.barOpen = $('[data-rit-sheet-open]', root);
      this.barCount = $('[data-rit-bar-count]', root);
      this.barPrice = $('[data-rit-bar-price]', root);
      this.backdrop = $('[data-rit-backdrop]', root);
      this.previewNote = this.note ? this.note.textContent.trim() : '';

      this.restore();
      this.bind();
      this.render();
      this.applyFilter(this.filter, false);
    }

    readCard(card) {
      const script = $('script[data-rit-line]', card);
      let data = null;
      try {
        data = script ? JSON.parse(script.textContent) : null;
      } catch (e) {
        data = null;
      }
      if (!data || !data.key || !Array.isArray(data.variants) || !data.variants.length) return;
      data.variants = data.variants.filter((v) => v && v.productId && v.productKey &&
        keyOf({ productId: v.productId, key: v.productKey, handle: v.handle }) === v.productKey);
      if (!data.variants.length) return;
      data.variants.forEach((v) => {
        ['plain', 'line', 'benefit', 'pack', 'dose'].forEach((k) => {
          v[k] = decode(v[k]);
        });
        v.price = Number(v.price) || 0;
        v.day = Number(v.day) || 0;
      });
      const split = (value) =>
        String(value || '')
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean);
      this.lines.set(data.key, {
        key: data.key,
        rituals: split(data.rituals),
        nutrients: split(data.nutrients),
        variants: data.variants,
        current: data.variants[0].productKey,
        card,
      });
    }

    variantOf(key, handle) {
      const line = this.lines.get(key);
      if (!line) return null;
      const productKey = keyOf(handle);
      return productKey ? line.variants.find((v) => v.productKey === productKey) || null : null;
    }

    /* ---------- state ---------- */

    restore() {
      const saved = storage.read();
      if (saved && saved.lines && typeof saved.lines === 'object') {
        Object.keys(saved.lines).forEach((key) => {
          const line = this.lines.get(key);
          if (!line) return;
          const variant = this.variantOf(key, saved.lines[key]);
          if (!variant) return;
          line.current = variant.productKey;
          this.selected.set(key, variant.productKey);
        });
      }
      let params = null;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        params = null;
      }
      if (!params) return;
      const add = String(params.get('add') || '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
      let matchedAdd = false;
      add.forEach((handle) => {
        this.lines.forEach((line, key) => {
          const variant = this.variantOf(key, handle);
          if (variant) {
            matchedAdd = true;
            line.current = variant.productKey;
            this.selected.set(key, variant.productKey);
          }
        });
      });
      const ritual = params.get('r');
      if (ritual && RITUALS[ritual]) this.filter = ritual;
      this.lines.forEach((line) => this.syncCardPack(line));
      if (matchedAdd) this.persist();
      this.deepLink = Boolean(matchedAdd || (ritual && RITUALS[ritual]));
    }

    persist() {
      const lines = {};
      this.selected.forEach((handle, key) => {
        lines[key] = handle;
      });
      storage.write({ v: 1, lines, at: Date.now() });
    }

    tierPct(n) {
      let pct = 0;
      this.tiers.forEach((t) => {
        if (n >= t.n) pct = t.p;
      });
      return pct;
    }

    nextTier(n) {
      return this.tiers.find((t) => t.n > n) || null;
    }

    totals() {
      const items = [];
      this.selected.forEach((handle, key) => {
        const line = this.lines.get(key);
        const variant = this.variantOf(key, handle);
        if (line && variant) items.push({ key, line, v: variant });
      });
      const n = items.length;
      const pct = this.tierPct(n);
      const subtotal = items.reduce((sum, it) => sum + it.v.price, 0);
      const discount = pct ? items.reduce((sum, it) => sum + Math.round((it.v.price * pct) / 100), 0) : 0;
      const payable = this.state === 'live' ? subtotal - discount : subtotal;
      return { items, n, pct, subtotal, discount, payable };
    }

    /* ---------- events ---------- */

    bind() {
      this.root.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-rit-toggle]');
        if (toggle) {
          const card = toggle.closest('[data-rit-card]');
          if (card) this.toggle(card.dataset.line);
          return;
        }
        const filter = event.target.closest('[data-rit-filter]');
        if (filter) {
          this.applyFilter(filter.dataset.ritFilter, true);
          return;
        }
        const preset = event.target.closest('[data-rit-preset-add]');
        if (preset) {
          this.addPreset(preset.closest('[data-rit-preset]'));
          return;
        }
        const remove = event.target.closest('[data-rit-remove]');
        if (remove) {
          this.remove(remove.dataset.ritRemove);
          return;
        }
        if (event.target.closest('[data-rit-slot]')) {
          this.goToGrid();
          return;
        }
        const addButton = event.target.closest('[data-rit-add]');
        if (addButton) {
          this.addToCart(addButton);
          return;
        }
        if (event.target.closest('[data-rit-sheet-open]')) {
          this.openSheet(event.target.closest('[data-rit-sheet-open]'));
          return;
        }
        if (event.target.closest('[data-rit-sheet-close]') || event.target.closest('[data-rit-backdrop]')) {
          this.closeSheet(true);
        }
      });

      this.root.addEventListener('change', (event) => {
        const radio = event.target.closest('[data-rit-pack]');
        if (!radio) return;
        const card = radio.closest('[data-rit-card]');
        const line = card && this.lines.get(card.dataset.line);
        if (!line) return;
        const variant = this.variantOf(line.key, radio.value);
        if (!variant) return;
        line.current = variant.productKey;
        if (this.selected.has(line.key)) {
          this.selected.set(line.key, variant.productKey);
          this.persist();
        }
        this.syncCardPack(line);
        this.render();
      });

      document.addEventListener('nl:ritual:pick', (event) => {
        const key = event.detail && event.detail.key;
        this.applyFilter(RITUALS[key] ? key : 'vse', true);
        this.scrollToBuilder();
      });

      /* other tab changed the saved ritual */
      window.addEventListener('storage', (event) => {
        if (event.key !== STORE_KEY) return;
        this.selected.clear();
        this.restore();
        this.render();
      });

      this.keydown = (event) => this.onSheetKey(event);
      this.bindSheetDrag();
      this.bindBar();

      const onViewport = () => {
        if (mqDesktop.matches && this.sheetOpen) this.closeSheet(false);
      };
      if (typeof mqDesktop.addEventListener === 'function') mqDesktop.addEventListener('change', onViewport);
      else if (typeof mqDesktop.addListener === 'function') mqDesktop.addListener(onViewport);

      if (this.deepLink) {
        window.requestAnimationFrame(() => window.setTimeout(() => this.scrollToBuilder(true), 120));
      }
    }

    toggle(key) {
      const line = this.lines.get(key);
      if (!line) return;
      if (this.selected.has(key)) this.selected.delete(key);
      else this.selected.set(key, line.current);
      this.msg('');
      this.persist();
      this.render();
    }

    remove(key) {
      if (!this.selected.has(key)) return;
      const rows = $$('[data-rit-remove]', this.list);
      const index = rows.findIndex((b) => b.dataset.ritRemove === key);
      this.selected.delete(key);
      this.msg('');
      this.persist();
      this.render();
      const after = $$('[data-rit-remove]', this.list);
      const target = after[Math.min(index, after.length - 1)] || $('.nl-rit-panel__title', this.panel);
      if (target) {
        if (!target.hasAttribute('tabindex') && target.tagName !== 'BUTTON') target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }

    addPreset(card) {
      if (!card) return;
      String(card.dataset.ritPreset || '')
        .split(',')
        .map((x) => x.trim())
        .forEach((key) => {
          const line = this.lines.get(key);
          if (line && !this.selected.has(key)) this.selected.set(key, line.current);
        });
      this.msg('');
      this.persist();
      this.render();
      if (this.bar && !mqDesktop.matches) {
        this.bar.classList.remove('is-pulse');
        void this.bar.offsetWidth;
        this.bar.classList.add('is-pulse');
      }
    }

    applyFilter(key, announce) {
      this.filter = RITUALS[key] ? key : 'vse';
      this.filterButtons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.ritFilter === this.filter)));
      let shown = 0;
      this.cards.forEach((card) => {
        const rituals = String(card.dataset.rituals || '').split(',');
        const show = this.filter === 'vse' || rituals.includes(this.filter);
        card.hidden = !show;
        if (show) shown += 1;
      });
      if (this.filterDesc && this.filterTexts[this.filter] !== undefined) {
        this.filterDesc.innerHTML = this.filterTexts[this.filter];
      }
      if (announce && this.summary) {
        this.summary.textContent = tr('shown', { count: shown });
      }
    }

    syncCardPack(line) {
      const card = line.card;
      if (!card) return;
      const variant = this.variantOf(line.key, line.current);
      if (!variant) return;
      $$('[data-rit-pack]', card).forEach((radio) => {
        radio.checked = keyOf(radio.value) === variant.productKey;
      });
      const price = $('[data-rit-price]', card);
      if (price) price.textContent = money(variant.price);
      const day = $('[data-rit-day]', card);
      if (day && variant.day) day.textContent = tr('per_day', { price: money(variant.day) });
      const dose = $('[data-rit-dose]', card);
      if (dose && variant.dose) dose.textContent = variant.dose;
    }

    scrollToBuilder(instant) {
      const head = $('.nl-rit-builder__head', this.root) || this.root;
      head.scrollIntoView({ behavior: instant || reduced() ? 'auto' : 'smooth', block: 'start' });
      const active = this.filterButtons.find((b) => b.getAttribute('aria-pressed') === 'true');
      if (active && !instant) window.setTimeout(() => active.focus({ preventScroll: true }), reduced() ? 0 : 450);
    }

    goToGrid() {
      if (this.sheetOpen) this.closeSheet(false);
      const target = this.cards.find((c) => !c.hidden && !this.selected.has(c.dataset.line) && $('[data-rit-toggle]', c));
      const anchor = target || this.grid;
      if (!anchor) return;
      anchor.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
      const button = target && $('[data-rit-toggle]', target);
      if (button) window.setTimeout(() => button.focus({ preventScroll: true }), reduced() ? 0 : 400);
    }

    msg(text, variant) {
      if (!this.msgBox) return;
      this.msgBox.textContent = text || '';
      this.msgBox.dataset.variant = variant || '';
    }

    /* ---------- render ---------- */

    render() {
      const t = this.totals();
      const live = this.state === 'live';
      const preview = this.state === 'preview';
      const next = this.nextTier(t.n);

      /* cards and presets */
      this.lines.forEach((line, key) => {
        const on = this.selected.has(key);
        line.card.classList.toggle('is-selected', on);
        const toggle = $('[data-rit-toggle]', line.card);
        if (toggle) toggle.setAttribute('aria-pressed', String(on));
      });
      this.presets.forEach((card) => {
        const keys = String(card.dataset.ritPreset || '')
          .split(',')
          .map((x) => x.trim())
          .filter((k) => this.lines.has(k));
        card.classList.toggle('is-done', keys.length > 0 && keys.every((k) => this.selected.has(k)));
      });

      /* head */
      if (this.count) this.count.textContent = t.n ? countLabel(t.n) : '';
      if (this.empty) this.empty.hidden = t.n > 0;

      /* items */
      if (this.list) {
        const frag = document.createDocumentFragment();
        t.items.forEach(({ key, line, v }) => {
          const li = el('li', 'nl-rit-item');
          li.dataset.key = key;
          const jar = el('span', `nl-rit-item__jar${v.imgMode === 'tight' ? ' is-tight' : ''}`);
          if (v.img) {
            const img = el('img');
            img.src = v.img;
            img.alt = '';
            img.width = 96;
            img.height = 96;
            // JM-06: eager, the list is built when the panel opens and a lazy image there stayed an empty circle
            img.loading = 'eager';
            img.decoding = 'async';
            jar.append(img);
          }
          const name = el('span', 'nl-rit-item__name');
          name.append(inlineName(v));
          if (line.variants.length > 1 && v.pack) name.append(el('span', 'nl-rit-item__pack', v.pack));
          const price = el('span', 'nl-rit-item__price', money(v.price));
          const remove = el('button', 'nl-rit-item__remove');
          remove.type = 'button';
          remove.dataset.ritRemove = key;
          remove.setAttribute('aria-label', tr('remove', { title: v.plain }));
          remove.innerHTML =
            '<svg class="nl-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
          li.append(jar, name, price, remove);
          frag.append(li);
        });
        this.list.replaceChildren(frag);
      }

      /* next slot */
      if (this.slot) {
        const more = t.n > 0 && t.n < this.lines.size;
        this.slot.hidden = !more;
        if (this.slotExtra) this.slotExtra.textContent = live && next ? tr('slot_discount', { percent: next.p }) : '';
      }

      /* ladder */
      if (this.ladder) {
        let reached = 0;
        this.tiers.forEach((tier) => {
          if (t.n >= tier.n) reached = tier.n;
        });
        $$('[data-rit-step]', this.ladder).forEach((step) => {
          const n = Number(step.dataset.ritStep);
          step.classList.toggle('is-on', t.n >= n);
          step.classList.toggle('is-current', n === reached);
        });
      }
      if (this.tierStatus) {
        let status = '';
        if (live && t.n > 0) {
          if (t.pct > 0) status = tr('current_discount', { percent: t.pct });
          if (next) {
            const missing = next.n - t.n;
            status += `${status ? ' ' : ''}${tr('next_discount', { count: countLabel(missing), percent: next.p })}`;
          } else if (t.pct > 0) {
            status += ` ${tr('highest_discount', { percent: t.pct })}`;
          }
        }
        this.tierStatus.textContent = status;
      }

      /* overlapping nutrients */
      if (this.overlap) {
        const counts = new Map();
        t.items.forEach(({ line }) => {
          line.nutrients.forEach((nutrient) => {
            if (this.ignore.has(nutrient)) return;
            counts.set(nutrient, (counts.get(nutrient) || 0) + 1);
          });
        });
        const doubled = [...counts.entries()].filter(([, c]) => c > 1);
        if (doubled.length) {
          const names = doubled.map(([k]) => nutrientName(k));
          const key = doubled.every(([, c]) => c === 2) ? 'overlap_two' : 'overlap_many';
          this.overlapText.textContent = tr(key, { nutrients: joinNutrients(names) });
          this.overlap.hidden = false;
        } else {
          this.overlap.hidden = true;
          this.overlapText.textContent = '';
        }
      }

      /* totals (only rewritten when the content changes, the box is aria-live) */
      if (this.totalsBox) {
        let html = '';
        if (t.n > 0) {
          const row = (label, value, cls = '') =>
            `<div class="nl-rit-totals__row${cls}"><span>${label}</span><span class="nl-rit-totals__v">${value}</span></div>`;
          if (live) {
            html += row(S.separately, money(t.subtotal));
            if (t.discount > 0) html += row(tr('discount_total', { percent: t.pct }), `−${NBSP}${money(t.discount)}`, ' is-discount');
          }
          html += row(S.total_html, money(t.payable), ' is-total');
        }
        if (html !== this.lastTotals) {
          this.totalsBox.innerHTML = html;
          this.lastTotals = html;
        }
      }

      /* free shipping, counted from the payable amount */
      if (this.ship) {
        this.ship.hidden = t.n === 0;
        if (t.n > 0) {
          this.shipText.textContent =
            t.payable >= this.threshold
              ? S.shipping_free
              : tr('shipping_remaining', { amount: money(this.threshold - t.payable) });
          this.ship.classList.toggle('is-free', t.payable >= this.threshold);
        }
      }

      /* buttons, note, bar */
      const label = this.busy ? S.adding : t.n > 1 ? S.add_ritual : (NL.strings && NL.strings.addToCart);
      if (this.addLabel) this.addLabel.textContent = label;
      this.addButtons.forEach((button) => {
        button.disabled = t.n === 0 || this.busy;
        button.setAttribute('aria-busy', String(this.busy));
        if (button.closest('[data-rit-bar]')) button.textContent = this.busy ? S.adding : (NL.strings && NL.strings.addToCart);
      });
      if (this.note) {
        if (live) this.note.textContent = t.discount > 0 ? tr('saving', { amount: money(t.discount) }) : '';
        else if (preview) this.note.textContent = this.previewNote;
        else this.note.textContent = '';
      }
      if (this.barCount) this.barCount.textContent = String(t.n);
      if (this.barPrice) this.barPrice.textContent = t.n ? money(t.payable) : '';
      if (this.bar) this.bar.classList.toggle('has-items', t.n > 0);
    }

    /* ---------- cart ---------- */

    async addToCart(button) {
      const t = this.totals();
      if (!t.n || this.busy) return;
      this.busy = true;
      this.msg('');
      this.render();
      const ritualId = `r-${Date.now().toString(36)}`;
      const items = t.items.map((it) => ({
        id: it.v.id,
        quantity: 1,
        properties: { _nl_ritual: ritualId, _nl_ritual_size: t.n },
      }));
      if (this.sheetOpen) this.closeSheet(false);
      try {
        if (NL.cart && typeof NL.cart.add === 'function') {
          // Optimistic lines in the drawer (assets/nl-w2-cart.js): title and image from each configurator card
          await NL.cart.add(items, {
            source: 'rituals',
            opener: button,
            optimistic: t.items.map((it) => ({ el: it.line.card, price: it.v.price })),
          });
        } else {
          const response = await fetch((NL.routes && NL.routes.cartAdd) || '/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (!response.ok) throw new Error(`cart ${response.status}`);
          window.location.href = (NL.routes && NL.routes.cart) || '/cart';
          return;
        }
        this.msg(S.added, 'success');
        // The open cart drawer already says so; a toast would sit unseen behind its modal backdrop
        const drawerOpen = document.querySelector('cart-drawer dialog[open]');
        if (!drawerOpen && typeof NL.toast === 'function') NL.toast(S.added, { variant: 'success' });
        document.dispatchEvent(
          new CustomEvent('nl:ritual:add', {
            detail: {
              lines: t.items.map((it) => ({ key: it.key, handle: it.v.handle, id: it.v.id, price: it.v.price })),
              tier: t.pct,
            },
          })
        );
        if (this.state === 'live' && t.discount > 0 && NL.cart && typeof NL.cart.get === 'function') {
          try {
            const cart = await NL.cart.get();
            if (!cart || !(Number(cart.total_discount) > 0)) {
              this.msg(S.discount_missing, 'error');
              console.warn('[nl-rituals] expected a set discount, cart.total_discount is 0', ritualId);
            }
          } catch (e) {
            /* the cart check is best effort */
          }
        }
      } catch (error) {
        this.msg(S.error, 'error');
      } finally {
        this.busy = false;
        this.render();
      }
    }

    /* ---------- mobile bar and bottom sheet ---------- */

    bindBar() {
      if (!this.bar || !('IntersectionObserver' in window)) {
        if (this.bar) this.bar.classList.add('is-visible');
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => this.bar.classList.toggle('is-visible', entry.isIntersecting));
        },
        { rootMargin: '0px 0px -25% 0px' }
      );
      observer.observe(this.root);
    }

    focusables() {
      return $$(
        'button:not([disabled]):not([hidden]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        this.panel
      ).filter((node) => node.offsetParent !== null || node === document.activeElement);
    }

    openSheet(opener) {
      if (mqDesktop.matches || !this.panel || this.sheetOpen) return;
      this.sheetOpen = true;
      this.opener = opener || document.activeElement;
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.style.removeProperty('--rit-drag');
      this.panel.classList.add('is-open');
      if (this.backdrop) {
        this.backdrop.hidden = false;
        window.requestAnimationFrame(() => this.backdrop.classList.add('is-visible'));
      }
      if (this.barOpen) this.barOpen.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('nl-rit-lock');
      document.addEventListener('keydown', this.keydown);
      const close = $('[data-rit-sheet-close]', this.panel);
      window.setTimeout(() => (close || this.panel).focus({ preventScroll: true }), reduced() ? 0 : 60);
    }

    closeSheet(restoreFocus) {
      if (!this.sheetOpen || !this.panel) return;
      this.sheetOpen = false;
      this.panel.classList.remove('is-open');
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.panel.style.removeProperty('--rit-drag');
      if (this.backdrop) {
        this.backdrop.classList.remove('is-visible');
        window.setTimeout(() => {
          if (!this.sheetOpen) this.backdrop.hidden = true;
        }, reduced() ? 0 : 300);
      }
      if (this.barOpen) this.barOpen.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('nl-rit-lock');
      document.removeEventListener('keydown', this.keydown);
      if (restoreFocus && this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
    }

    onSheetKey(event) {
      if (!this.sheetOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSheet(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = this.focusables();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !this.panel.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }

    bindSheetDrag() {
      const grab = $('[data-rit-grab]', this.root);
      const head = $('.nl-rit-panel__head', this.root);
      if (!this.panel) return;
      let startY = 0;
      let dy = 0;
      let dragging = false;
      const down = (event) => {
        if (!this.sheetOpen || event.target.closest('button')) return;
        dragging = true;
        startY = event.clientY;
        dy = 0;
        this.panel.classList.add('is-dragging');
        if (event.currentTarget.setPointerCapture) event.currentTarget.setPointerCapture(event.pointerId);
      };
      const move = (event) => {
        if (!dragging) return;
        dy = Math.max(0, event.clientY - startY);
        this.panel.style.setProperty('--rit-drag', `${dy}px`);
      };
      const up = () => {
        if (!dragging) return;
        dragging = false;
        this.panel.classList.remove('is-dragging');
        if (dy > 90) this.closeSheet(true);
        else this.panel.style.removeProperty('--rit-drag');
      };
      [grab, head].forEach((node) => {
        if (!node) return;
        node.addEventListener('pointerdown', down);
        node.addEventListener('pointermove', move);
        node.addEventListener('pointerup', up);
        node.addEventListener('pointercancel', up);
      });
    }
  }

  /* ==========================================================================
     Boot
     ========================================================================== */

  /* Výše zvýhodnění v náhledu jen když ji sekce Zvýhodnění výslovně ukazuje (preview_show_values, výchozí vypnuto).
     Bez sekce nebo s vypnutým nastavením zůstanou procenta v panelu a v dotazech skrytá (fail-closed). */
  const initValues = () => {
    const show = Boolean(document.querySelector('[data-rit-show-values="true"]'));
    if (!show) return;
    $$('[data-rit-value]').forEach((node) => {
      node.hidden = false;
    });
    $$('[data-rit-value-alt]').forEach((node) => {
      node.hidden = true;
    });
  };

  const boot = () => {
    initMarkers();
    initVideo();
    initValues();
    $$('[data-rit-builder]').forEach((root) => {
      if (!root.ritual) root.ritual = new RitualBuilder(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  /* theme editor: sections re-render */
  document.addEventListener('shopify:section:load', () => boot());
})();
